import { downloadFile } from '@client/utils/download';

const SENSITIVE_KEYS =
  /^(api[_-]?key|api[_-]?secret|secret[_-]?key|password|passwd|token|access[_-]?token|refresh[_-]?token|auth[_-]?token|bearer|authorization|client[_-]?secret|private[_-]?key|credentials|session[_-]?id|cookie)$/i;

const SENSITIVE_VALUE_PATTERNS = [/^Bearer\s+.+/i, /^Basic\s+.+/i, /^sk[_-].{10,}/, /^pk[_-].{10,}/];

const redactString = (value) => {
  if (typeof value !== 'string') return value;
  for (const pattern of SENSITIVE_VALUE_PATTERNS) {
    if (pattern.test(value)) return '********';
  }
  return value;
};

const redactSensitiveValues = (data) => {
  if (data === null || data === undefined) return data;
  if (typeof data === 'string') return redactString(data);
  if (Array.isArray(data)) return data.map(redactSensitiveValues);

  if (typeof data === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(data)) {
      if (SENSITIVE_KEYS.test(key)) {
        cleaned[key] = '********';
      } else if (typeof value === 'object' && value !== null) {
        cleaned[key] = redactSensitiveValues(value);
      } else {
        cleaned[key] = redactString(value);
      }
    }
    return cleaned;
  }

  return data;
};

const formatExecutionTime = (ms) => {
  if (ms === undefined || ms === null) return 'N/A';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return `${minutes}m ${seconds}s`;
};

const generateCurl = (entry) => {
  const url = `${entry.baseURL}${entry.path}`;
  const parts = [`curl -X ${entry.method} '${url}'`];

  parts.push(`  -H 'Content-Type: application/json'`);
  parts.push(`  -H 'Authorization: Bearer <TOKEN>'`);

  if (entry.body && entry.body !== '[REDACTED]') {
    const safeBody = redactSensitiveValues(entry.body);
    const bodyStr = JSON.stringify(safeBody).replace(/'/g, "'\\''");
    parts.push(`  -d '${bodyStr}'`);
  }

  if (entry.query) {
    const safeQuery = redactSensitiveValues(entry.query);
    const queryStr = new URLSearchParams(safeQuery).toString();
    if (queryStr) {
      parts[0] = `curl -X ${entry.method} '${url}?${queryStr}'`;
    }
  }

  return parts.join(' \\\n');
};

const formatRequestLog = (logs) => {
  if (!logs?.length) return '  No API requests recorded.';

  return logs
    .map((entry, i) => {
      const lines = [
        `  Request ${i + 1}: ${entry.method} ${entry.path}`,
        `  Timestamp: ${entry.timestamp}`,
        `  Response:  ${entry.response ? `${entry.response.status} ${entry.response.statusText || entry.response.message || ''}`.trim() : 'No response'}`,
        ``,
        `  Payload:`,
        `  ${entry.body === '[REDACTED]' ? '[REDACTED]' : JSON.stringify(redactSensitiveValues(entry.body), null, 2).split('\n').join('\n  ')}`,
        ``,
        `  cURL:`,
        `  ${generateCurl(entry).split('\n').join('\n  ')}`,
        ``
      ];

      return lines.join('\n');
    })
    .join('\n  ---\n\n');
};

const formatSection = (title, content) => [`--- ${title} ---`, content, ``];

/**
 * Downloads a debug report for a failed operation.
 *
 * @param {object} options
 * @param {string} options.title - Report title (e.g. "Workflow Step", "Campaign Export")
 * @param {string} options.filename - Download filename (without extension)
 * @param {object} options.error - The error object ({ message, stack })
 * @param {number} [options.executionTime] - Execution time in ms
 * @param {Array} [options.requestLogs] - API request log entries
 * @param {Object<string, string|number>} [options.details] - Key-value pairs for the main details section
 * @param {Array<{ label: string, data: * }>} [options.sections] - Additional named data sections
 */
export const downloadDebugReport = ({
  title = 'DEBUG REPORT',
  filename = 'debug-report',
  error,
  executionTime,
  requestLogs = [],
  details = {},
  sections = []
}) => {
  const timestamp = new Date().toISOString();

  const lines = [
    `=====================================`,
    `  ${title.toUpperCase()}`,
    `=====================================`,
    ``,
    `Generated: ${timestamp}`,
    `Browser:   ${navigator.userAgent}`,
    `URL:       ${window.location.href}`,
    ``
  ];

  if (Object.keys(details).length) {
    const safeDetails = redactSensitiveValues(details);
    const detailLines = Object.entries(safeDetails).map(([key, value]) => `${key}: ${value ?? 'N/A'}`);
    lines.push(...formatSection('Details', detailLines.join('\n')));
  }

  if (executionTime !== undefined) {
    lines.push(`Execution Time: ${formatExecutionTime(executionTime)}`, ``);
  }

  lines.push(
    ...formatSection(
      'Error',
      [
        `Message: ${error?.message || 'Unknown error'}`,
        ...(error?.stack ? [``, `Stack Trace:`, error.stack] : [])
      ].join('\n')
    )
  );

  lines.push(...formatSection(`API Requests (${requestLogs.length})`, formatRequestLog(requestLogs)));

  for (const section of sections) {
    const safeData = redactSensitiveValues(section.data);
    const content = typeof safeData === 'string' ? safeData : JSON.stringify(safeData, null, 2);
    lines.push(...formatSection(section.label, content));
  }

  lines.push(`=====================================`, `  END OF REPORT`, `=====================================`);

  const content = lines.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });

  downloadFile(blob, `${filename}-${new Date().toISOString().slice(0, 10)}.txt`);
};
